import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Layers, Settings2, AlertTriangle, Lightbulb } from "lucide-react";

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString()}`);
const STATUSES = ["trial", "active", "past_due", "canceled", "suspended"];

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [editTiers, setEditTiers] = useState(false);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [{ data: c }, { data: t }] = await Promise.all([
        api.get("/superadmin/clients"),
        api.get("/superadmin/tiers"),
      ]);
      setClients(c.clients || []);
      setTiers(t.tiers || []);
      setCatalog(t.catalog || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load clients"); }
  };
  useEffect(() => { load(); }, []);

  const assign = async (oid, patch) => {
    try {
      await api.put(`/superadmin/orgs/${oid}/tier`, patch);
      toast.success("Updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };

  const openTierEditor = () => { setDraft(JSON.parse(JSON.stringify(tiers))); setEditTiers(true); };
  const setDraftField = (i, k, v) => setDraft((d) => d.map((t, idx) => idx === i ? { ...t, [k]: v } : t));
  const toggleFeature = (i, key) => setDraft((d) => d.map((t, idx) => {
    if (idx !== i) return t;
    const has = (t.features || []).includes(key);
    return { ...t, features: has ? t.features.filter((f) => f !== key) : [...(t.features || []), key] };
  }));
  const saveTiers = async () => {
    setSaving(true);
    try {
      const payload = {
        tiers: draft.map((t) => ({
          key: t.key, name: t.name, price: Number(t.price) || 0,
          truck_min: Number(t.truck_min) || 0,
          truck_max: t.truck_max === "" || t.truck_max == null ? null : Number(t.truck_max),
          features: t.features || [],
        })),
      };
      await api.put("/superadmin/tiers", payload);
      toast.success("Tiers & pricing saved");
      setEditTiers(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save tiers"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="clients-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-primary-orange" />
          <h1 className="heading text-lg font-black uppercase tracking-wide">Clients &amp; Tiers</h1>
        </div>
        <Button data-testid="manage-tiers-btn" variant="outline" className="rounded-none text-xs" onClick={openTierEditor}>
          <Settings2 className="w-4 h-4 mr-1.5" /> Manage tiers &amp; pricing
        </Button>
      </div>

      {/* Tier summary cards */}
      <div className="grid sm:grid-cols-3 gap-3">
        {tiers.map((t) => (
          <div key={t.key} className="border border-industrial bg-surface p-4" data-testid={`tier-card-${t.key}`}>
            <div className="text-xs uppercase tracking-widest text-muted-industrial">{t.name}</div>
            <div className="text-2xl font-black text-primary-orange">{money(t.price)}<span className="text-xs text-muted-industrial">/mo</span></div>
            <div className="text-xs text-muted-industrial mt-1">{t.truck_max == null ? `${t.truck_min}+ trucks` : `${t.truck_min}–${t.truck_max} trucks`}</div>
            <div className="text-[11px] text-muted-industrial mt-2">{(t.features || []).length} features</div>
          </div>
        ))}
      </div>

      {/* Clients table */}
      <div className="border border-industrial overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-black/20 text-muted-industrial uppercase tracking-wider">
            <tr>
              <th className="text-left p-2">Tenant</th>
              <th className="text-left p-2">Tier</th>
              <th className="text-right p-2">Trucks</th>
              <th className="text-right p-2">Monthly</th>
              <th className="text-left p-2">Billing</th>
              <th className="text-left p-2">Trial ends</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.id} className="border-t border-industrial/60" data-testid={`client-row-${c.slug}`}>
                <td className="p-2 font-bold">{c.name}</td>
                <td className="p-2">
                  <Select value={c.tier || "none"} onValueChange={(v) => assign(c.id, { tier: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid={`tier-select-${c.slug}`} className="h-8 rounded-none bg-transparent border-industrial text-xs w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Full access (legacy)</SelectItem>
                      {tiers.map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {c.tier_mismatch && (
                    <button
                      type="button"
                      data-testid={`tier-suggest-${c.slug}`}
                      onClick={() => assign(c.id, { tier: c.suggested_tier })}
                      title={`${c.truck_count} trucks fit the ${c.suggested_tier_name} band — click to apply`}
                      className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-amber-400 border border-amber-500/50 bg-amber-500/10 px-1.5 py-0.5 hover:bg-amber-500/20"
                    >
                      <Lightbulb className="w-3 h-3" /> Suggest: {c.suggested_tier_name} →
                    </button>
                  )}
                </td>
                <td className="p-2 text-right mono">
                  <span className={c.over_limit ? "text-red-400 font-bold" : ""}>{c.truck_count}</span>
                  {c.over_limit && <AlertTriangle className="w-3 h-3 inline ml-1 text-red-400" title="Over tier truck limit" />}
                </td>
                <td className="p-2 text-right mono">{money(c.monthly_rate)}</td>
                <td className="p-2">
                  <Select value={c.billing_status || "active"} onValueChange={(v) => assign(c.id, { billing_status: v })}>
                    <SelectTrigger data-testid={`billing-select-${c.slug}`} className="h-8 rounded-none bg-transparent border-industrial text-xs w-28 capitalize"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-2">
                  <Input type="date" data-testid={`trial-end-${c.slug}`} defaultValue={c.trial_ends_at ? c.trial_ends_at.slice(0, 10) : ""}
                    onBlur={(e) => assign(c.id, { trial_ends_at: e.target.value })}
                    className="h-8 rounded-none bg-transparent border-industrial text-xs w-36" />
                </td>
              </tr>
            ))}
            {clients.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-muted-industrial">No clients yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Tier editor */}
      <Dialog open={editTiers} onOpenChange={setEditTiers}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-4xl max-h-[90vh] overflow-auto" data-testid="tier-editor">
          <DialogHeader><DialogTitle className="mono uppercase tracking-widest text-sm">Manage tiers, pricing &amp; features</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-3 gap-4">
            {draft.map((t, i) => (
              <div key={t.key} className="border border-industrial p-3 space-y-2" data-testid={`tier-edit-${t.key}`}>
                <Input value={t.name} onChange={(e) => setDraftField(i, "name", e.target.value)} className="rounded-none bg-transparent border-industrial font-bold" />
                <div>
                  <Label className="label-tag">Price / month ($)</Label>
                  <Input type="number" data-testid={`tier-price-${t.key}`} value={t.price} onChange={(e) => setDraftField(i, "price", e.target.value)} className="rounded-none bg-transparent border-industrial" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="label-tag">Trucks min</Label><Input type="number" value={t.truck_min} onChange={(e) => setDraftField(i, "truck_min", e.target.value)} className="rounded-none bg-transparent border-industrial" /></div>
                  <div><Label className="label-tag">Trucks max</Label><Input type="number" placeholder="∞" value={t.truck_max ?? ""} onChange={(e) => setDraftField(i, "truck_max", e.target.value)} className="rounded-none bg-transparent border-industrial" /></div>
                </div>
                <div className="pt-1">
                  <Label className="label-tag">Features included</Label>
                  <div className="space-y-1 mt-1 max-h-56 overflow-auto">
                    {catalog.map((f) => (
                      <label key={f.key} className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" data-testid={`tier-feat-${t.key}-${f.key}`} checked={(t.features || []).includes(f.key)} onChange={() => toggleFeature(i, f.key)} />
                        {f.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="rounded-none text-xs" onClick={() => setEditTiers(false)}>Cancel</Button>
            <Button data-testid="save-tiers-btn" className="rounded-none bg-primary-orange text-black text-xs font-bold" disabled={saving} onClick={saveTiers}>{saving ? "Saving…" : "Save tiers"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
